import { ENTER } from '@wordpress/keycodes';
import type {
  ColumnApi,
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  SortModelItem,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';
import { uniq, values } from 'lodash';
import { indexBy, pipe } from 'ramda';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { InferredProgressValue } from 'api/project';
import { Task as ITaskApi, TaskAPI } from 'api/tasks';
import type { Filter } from 'components/molecules/filters/types';
import { FilterGroupType } from 'components/pages/types';
import { EXTRA_DETAILS } from 'constant';
import { taskNormalizer } from 'data/tasks/schema';
import {
  useWithAccess,
  useProjectAsRBACResource,
} from 'hooks/rbac/useWithAccess';
import {
  calculateProgressPercent,
  getFieldMapReducer as getProjectFieldMapReducer,
  lastStatusUpdatedAt,
} from 'routes/projects/list-v2/transformer';
import { logger } from 'services';
import {
  CreatableContent,
  CreatableEditableRefreshableContent,
  Loaded,
  Loading,
} from 'stores/core';
import { ProjectField, TaskField } from 'stores/fields';
import { loadedAllTasks, Task } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { getFieldString } from 'utils/export';
import { toStoreTask } from 'utils/to-store-task';
import { useTaskSelectionContext } from '../sprint-planning/new-sprint/task-selection/context';
import { QueryCombinerType } from './combiner';
import { FilterableField, SourceType } from './fields';
import { FieldFilter, useFilterEffect } from './filters';
import type { GroupCriteria, GroupedFilter } from './grouped-filter-popover';
import { useAllTasksPaginatedSocketUpdates } from './use-all-tasks-paginated-socket-updates';

export const defaultGroupedFilter = (
  defaultGroup = FilterGroupType.Task,
): GroupedFilter => ({
  type: QueryCombinerType.All,
  defaultGroup,
  groupCriteria: [],
});

export const isGroupFilter = (
  filter: GroupedFilter | Filter,
): filter is GroupedFilter => {
  return (<GroupedFilter>filter).groupCriteria != null;
};

export const convertGroupCriteriaToNonGroupFilter = ({
  criteria: groupCriteria,
  type,
}: {
  criteria: GroupCriteria[];
  type: QueryCombinerType;
}): Filter => {
  return {
    criteria: groupCriteria.map(({ criteria }) => criteria),
    type,
  };
};

// TODO: Move to backend?!
export const default_column_order = [
  'checkbox',
  'status',
  'project',
  'pointsLinear',
  'pointsExponential',
  'pointsFibonacci',
  'pointsCustom',
  'projectPhaseId',
  'assignee',
  'priority',
  'startDate',
  'dueDate',
  'effort',
  'timeEntryMinutes',
  'progress',
  'atRisk',
  'startDateActual',
  'completedAt',
  'responsible',
  'createdBy',
  'createdAt',
  'jiraIntegration',
];

export const readonly_fields = [
  'createdBy',
  'createdAt',
  'atRisk',
  'project',
  'responsible',
  'jiraIntegration',
  'timeEntryMinutes',
];
export const hideFromExternalUser = ['timeEntryMinutes'];

export const suppressEnterKey = ({
  editing,
  event,
}: SuppressKeyboardEventParams): boolean => {
  if (editing && event.keyCode === ENTER) {
    return true;
  }

  return false;
};

export const suppressEnterAndTabKey = ({
  editing,
  event,
}: SuppressKeyboardEventParams): boolean => {
  if (
    editing &&
    (event.code.toLowerCase() === 'enter' || event.code.toLowerCase() === 'tab')
  ) {
    return true;
  }

  return false;
};

export const toRows = (
  task_ids: number[],
  task_lookup: { [id: number]: CreatableEditableRefreshableContent<Task> },
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  task_ids.forEach((id) => {
    const lookup = task_lookup[id];

    if (!lookup) {
      return;
    }
    const [status, task] = lookup;

    if (!task) {
      return;
    }

    if (
      status === 'creating' ||
      status === 'loaded' ||
      status === 'updating' ||
      status === 'refreshing'
    ) {
      rows.push({ ...task });
    }
  });

  return rows;
};

type FieldId = number;
interface ConstructNormalizedTaskParams {
  taskList?: ITaskApi[];
  fieldList: Record<FieldId, TaskField>;
  isLoading: boolean;
}

type TaskId = number;

const constructNormalizedTask = ({
  taskList,
  fieldList,
  isLoading,
}: ConstructNormalizedTaskParams): {
  byId: Record<TaskId, CreatableContent<Task>>;
  ids: Loading | Loaded<TaskId[]>;
} => {
  if (!taskList) {
    return { ids: Loading(), byId: {} };
  }
  const updatedTaskList: ITaskApi[] = taskList.map((task) => ({
    ...task,
    fields: task.fields.map(withValidCustomFieldsForData),
  }));
  const {
    entities: { tasks },
  } = taskNormalizer(updatedTaskList);
  const to_row = toStoreTask(fieldList, tasks);

  const { payload: allMyTask } = loadedAllTasks(values(tasks).map(to_row));

  return {
    byId: pipe(
      () => allMyTask.map(Loaded),
      indexBy((x) => x[1].taskId),
    )(),
    ids: !isLoading
      ? Loaded(updatedTaskList?.map((task) => task.taskId))
      : Loading(),
  };
};

const transformProjectFields = (
  projectField: Record<FieldId, ProjectField>,
  tasks?: ITaskApi[],
) => {
  const toFieldMap = getProjectFieldMapReducer(projectField);

  return tasks?.map(({ project }) => {
    const { fields } = project;
    const fieldsMap = (fields ?? []).reduce(toFieldMap, {});
    const progressPercent = calculateProgressPercent(
      project.progressStatus,
      project.totalTaskCount,
    );

    return {
      ...project,
      ...fieldsMap,
      currentPhases:
        project.currentPhases && project.currentPhases.length > 0
          ? project.currentPhases
          : null,
      progressPercent,
      meta: {
        customer: project.customer,
      },
      arr: project.annualizedRecurringRevenue,
      customer: project.customer?.companyId,
      projectOwner: project.projectOwner?.userId,
      inferredProgress:
        project.inferredProgress?.value === InferredProgressValue.None
          ? undefined
          : project.inferredProgress?.value,
      estimatedDueDate: project.inferredProgressDays,
      teamMembers: (project.teamMembers ?? []).map(({ userId }) => userId),
      projectHours: {
        budgetMinutes: project.budgetedMinutes,
        total_minutes: project.totalMinutes,
        project_name: project.projectName,
      },
      lastStatusUpdate: lastStatusUpdatedAt(project.pastProjectStatus ?? []),
    };
  });
};

const PROJECT_COLUMN_PREFIX = 'project-';

const getSortStringForProjectField = (
  projectLookup: Record<string, FilterableField>,
  sortedField: SortModelItem,
): string => {
  let sortColName = '';

  if (Object.keys(projectLookup).length <= 0) {
    return '';
  }

  sortColName = sortedField?.colId.split(PROJECT_COLUMN_PREFIX)[1];
  const field = projectLookup[sortColName];

  if (!field) {
    return '';
  }

  return `${getFieldString(field)}.${sortedField.sort}`;
};

const getSortAsString = (
  lookup: Record<string, FilterableField>,
  projectLookup: Record<string, FilterableField>,
  sortOrder?: SortModelItem[],
): string => {
  const [sortedField] = sortOrder || [];

  if (!sortedField) {
    return `${SourceType.Task}*${SourceType.Sprint}.desc`;
  }

  if (sortedField?.colId.startsWith(PROJECT_COLUMN_PREFIX)) {
    return getSortStringForProjectField(projectLookup, sortedField);
  }

  let sortColName = `${SourceType.Task}*${SourceType.Sprint}.desc`;

  switch (sortedField?.colId) {
    case 'status':
      sortColName = 'taskName';
      break;
    case 'projectPhaseId':
      sortColName = 'phaseName';

      return `${sortColName}.${sortedField.sort}`;
    default:
      sortColName = sortedField?.colId;
  }

  const field = lookup[sortColName];

  if (!field) {
    return `${SourceType.Task}*${SourceType.Sprint}.desc`;
  }

  return `${getFieldString(field)}.${sortedField.sort}`;
};

const dataSetterFunction =
  ({
    taskFields,
    socketSubscriber,
    success,
    projectId,
  }: {
    taskFields: Record<FieldId, TaskField>;
    socketSubscriber: (newProjectIds: number[]) => void;
    success: (params: LoadSuccessParams) => void;
    projectId: number;
  }) =>
  (data: ITaskApi[], count: number, showProvisionalRow = true) => {
    const { byId: taskLookup, ids: allMyIds } = constructNormalizedTask({
      fieldList: taskFields,
      isLoading: false,
      taskList: data,
    });

    if (allMyIds[0] === 'loading') {
      return;
    }
    const tasks = toRows(allMyIds[1], taskLookup);

    socketSubscriber([projectId]);
    success({
      rowData: [
        ...(showProvisionalRow
          ? [
              {
                project: projectId,
                taskId: 'provisional',
                taskTree: [{ taskId: 'provisional' }],
              },
            ]
          : []),
        ...tasks,
      ],
      rowCount: showProvisionalRow ? count + 1 : count, // +1 for provisional row
    });
  };

const failureFunction =
  ({ setIsInitialLoading, fail }) =>
  (e: any) => {
    logger.log('error downloading content', e);
    setIsInitialLoading(false);
    fail();
  };

export const useServerSideDataSourceforTaskList = (
  taskFields: Record<FieldId, TaskField>,
  filter: Filter,
  lookup: Record<string, FilterableField>,
  projectId: number,
): {
  onGridReady: (event: GridReadyEvent) => void;
  gridApi: GridApi | undefined;
  isInitialLoading: boolean;
  gridColumnApi: ColumnApi | undefined;
} => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>();
  const socketSubscriber = useAllTasksPaginatedSocketUpdates({
    fields: taskFields,
    gridApi,
  });
  const filterRef = useRef(filter);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  useFilterEffect(() => {
    setIsInitialLoading(true);
  });

  const projectResource = useProjectAsRBACResource(projectId);
  const { hasAccessToResource } = useWithAccess();
  const canCreateTask = hasAccessToResource({
    rule: 'TASK:CREATE',
    resourceData: projectResource,
  });

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setIsInitialLoading(true);
      setGridApi(event.api);
      setGridColumnApi(event.columnApi);
      event.api.setServerSideDatasource({
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, success, fail } = params;
          const { endRow, startRow, sortModel, groupKeys } = request;
          const validFilter: GroupCriteria[] = filterRef.current.criteria
            .map((criteria) => ({
              id: FilterGroupType.Task,
              criteria,
            }))
            .filter((x) => FieldFilter.isValid(x.criteria));
          const setDataToGrid = dataSetterFunction({
            projectId,
            socketSubscriber,
            success,
            taskFields,
          });
          const failureCallback = failureFunction({
            fail,
            setIsInitialLoading,
          });

          if (validFilter.length > 0) {
            TaskAPI.getPaginatedTaskListWithProject({
              startRow: startRow ?? 0,
              endRow: endRow ?? 100,
              filter: validFilter,
              filterType: filterRef.current.type,
              sort: getSortAsString(lookup, {}, sortModel),
              projectId,
            })
              .then(({ count, data }) => {
                setIsInitialLoading(false);
                setDataToGrid(data, count, canCreateTask && startRow === 0);
              })
              .catch(failureCallback);
          } else if (groupKeys.length > 0 && validFilter.length === 0) {
            TaskAPI.getSubTasks(Number(groupKeys[groupKeys.length - 1]))
              .then((tasksData) => {
                setIsInitialLoading(false);
                setDataToGrid(tasksData, tasksData.length, false);
              })
              .catch(failureCallback);
          } else {
            TaskAPI.getPaginatedTaskListWithProject({
              startRow: startRow ?? 0,
              endRow: endRow ?? 100,
              filter: validFilter,
              filterType: filterRef.current.type,
              sort: getSortAsString(lookup, {}, sortModel),
              projectId,
              isEverything: true, // this is done to request BE only for parent tasks. Subtasks shd be sent only when a group is opened i.e., groupKeys have some value
            })
              .then(({ count, data }) => {
                setIsInitialLoading(false);
                setDataToGrid(data, count, canCreateTask && startRow === 0);
              })
              .catch(failureCallback);
          }
        },
      });
    },
    [projectId, socketSubscriber, taskFields, lookup, canCreateTask],
  );

  return {
    onGridReady,
    gridApi,
    isInitialLoading,
    gridColumnApi,
  };
};
export const useServerSideDataSource = (
  taskFields: Record<FieldId, TaskField>,
  projectFields: Record<FieldId, ProjectField>,
  filter: GroupedFilter,
  lookup: Record<string, FilterableField>,
  projectLookup: Record<string, FilterableField>,
): {
  onGridReady: (event: GridReadyEvent) => void;
  gridApi: GridApi | undefined;
  isInitialLoading: boolean;
  gridColumnApi: ColumnApi | undefined;
  isError: boolean;
} => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>();
  const [isError, setIsError] = useState(false);
  const socketSubscriber = useAllTasksPaginatedSocketUpdates({
    fields: taskFields,
    gridApi,
  });
  // Injecting external filter to ServerSideDataSource is very difficult. Have to find a way
  const filterRef = useRef(filter);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useFilterEffect(() => {
    setIsInitialLoading(true);
  });

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setIsInitialLoading(true);
      setGridApi(event.api);
      setGridColumnApi(event.columnApi);
      event.api.setServerSideDatasource({
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, success, fail } = params;
          const { endRow, startRow, sortModel } = request;
          const validFilter = filterRef.current.groupCriteria.filter((x) =>
            FieldFilter.isValid(x.criteria),
          );

          TaskAPI.getPaginatedTaskList({
            startRow: startRow ?? 0,
            endRow: endRow ?? 100,
            filter: validFilter,
            filterType: filterRef.current.type,
            sort: getSortAsString(lookup, projectLookup, sortModel),
          })
            .then((data) => {
              const { data: taskList, count } = data;

              setIsError(false);
              setIsInitialLoading(false);
              const { byId: taskLookup, ids: allMyIds } =
                constructNormalizedTask({
                  fieldList: taskFields,
                  isLoading: false,
                  taskList,
                });

              if (allMyIds[0] === 'loading') {
                return;
              }

              const tasks = toRows(allMyIds[1], taskLookup);

              const projectIds = taskList.map((t) => t.project.projectId);

              socketSubscriber(uniq(projectIds));
              const projects = transformProjectFields(projectFields, taskList);
              const taskWithProjects = tasks.map((task) => ({
                ...task,
                [EXTRA_DETAILS]: {
                  [SourceType.Project]: projects?.find(
                    ({ projectId }) => projectId === task.project,
                  ),
                },
              }));

              success({ rowData: taskWithProjects, rowCount: count });
            })
            .catch((e) => {
              setIsError(true);
              logger.error('error downloading all tasks content', {
                error: e,
                filter: validFilter.toString(),
                filterType: filterRef.current.type,
              });
              setIsInitialLoading(false);
              fail();
            })
            .finally(() => {
              setIsInitialLoading(false);
            });
        },
      });
    },
    [lookup, projectLookup, taskFields, socketSubscriber, projectFields],
  );

  return {
    onGridReady,
    gridApi,
    isInitialLoading,
    gridColumnApi,
    isError,
  };
};

type ServerSideDataSourceType = {
  taskFields: Record<FieldId, TaskField>;
  projectFields: Record<FieldId, ProjectField>;
  filter: GroupedFilter;
  lookup: Record<string, FilterableField>;
  projectLookup: Record<string, FilterableField>;
  gridApi: GridApi | undefined;
  setGridApis: (event: GridReadyEvent) => void;
  searchValue: string;
  projectId?: number;
};

export const textSearchAsString = (textSearch: string) => {
  return textSearch ? `task*taskName.contains=${textSearch}` : '';
};

export const transformTasksData = (
  data: ITaskApi[],
  taskFields: Record<number, TaskField>,
  projectFields: Record<number, ProjectField>,
) => {
  const { byId: taskLookup, ids: allMyIds } = constructNormalizedTask({
    fieldList: taskFields,
    isLoading: false,
    taskList: data,
  });

  if (allMyIds[0] === 'loading') {
    return null;
  }

  const tasks = toRows(allMyIds[1], taskLookup);

  const projectIdsList = data.map((t) => t.project.projectId);

  const projects = transformProjectFields(projectFields, data);
  const taskWithProjects = tasks.map((task) => ({
    ...task,
    [EXTRA_DETAILS]: {
      [SourceType.Project]: projects?.find(
        (project) => project.projectId === task.project,
      ),
    },
  }));

  return { projectIdsList, taskWithProjects };
};

export const useServerSideDataSourceForSprint = ({
  taskFields,
  projectFields,
  filter,
  lookup,
  projectLookup,
  gridApi,
  setGridApis,
  searchValue,
  projectId,
}: ServerSideDataSourceType): {
  onGridReady: (event: GridReadyEvent) => void;
  isInitialLoading: boolean;
} => {
  const socketSubscriber = useAllTasksPaginatedSocketUpdates({
    fields: taskFields,
    gridApi,
  });

  // Injecting external filter to ServerSideDataSource is very difficult. Have to find a way
  const filterRef = useRef(filter);
  const searchRef = useRef(searchValue);
  const { selectedTasks } = useTaskSelectionContext();
  const selectedRowsRef = useRef(selectedTasks);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  useFilterEffect(() => {
    setIsInitialLoading(true);
  });

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    searchRef.current = searchValue;
  }, [searchValue]);

  useEffect(() => {
    selectedRowsRef.current = selectedTasks;
  }, [selectedTasks]);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setIsInitialLoading(true);
      setGridApis(event);

      event.api.setServerSideDatasource({
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, success, fail } = params;
          const { endRow, startRow, sortModel } = request;
          const validFilter = filterRef.current.groupCriteria.filter((x) =>
            FieldFilter.isValid(x.criteria),
          );

          TaskAPI.getPaginatedTaskListWithProject({
            startRow: startRow ?? 0,
            endRow: endRow ?? 100,
            filter: validFilter,
            filterType: filterRef.current.type,
            sort: getSortAsString(lookup, projectLookup, sortModel),
            searchValue: textSearchAsString(searchRef.current),
            projectId,
          })
            .then(({ count, data }) => {
              setIsInitialLoading(false);

              const transformedData = transformTasksData(
                data,
                taskFields,
                projectFields,
              );

              if (transformedData === null) {
                return;
              }
              const { projectIdsList, taskWithProjects } = transformedData;

              socketSubscriber(uniq(projectIdsList));

              success({ rowData: taskWithProjects, rowCount: count });
            })
            .catch((e) => {
              logger.log('error downloading content', e);
              setIsInitialLoading(false);
              fail();
            });
        },
      });
    },
    [
      lookup,
      projectLookup,
      taskFields,
      socketSubscriber,
      projectFields,
      setGridApis,
      projectId,
    ],
  );

  return {
    onGridReady,
    isInitialLoading,
  };
};

export const useServerSideDataSourceForDashBoard = (
  taskFields: Record<FieldId, TaskField>,
  projectFields: Record<FieldId, ProjectField>,
  dashboardFilter: SimpleSearchFilter,
  widgetFilter: SimpleSearchFilter,
  lookup: Record<string, FilterableField>,
  projectLookup: Record<string, FilterableField>,
  widgetId: number,
): {
  onGridReady: (event: GridReadyEvent) => void;
  gridApi: GridApi | undefined;
  isInitialLoading: boolean;
  gridColumnApi: ColumnApi | undefined;
  isError: boolean;
} => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>();
  const [isError, setIsError] = useState(false);
  const socketSubscriber = useAllTasksPaginatedSocketUpdates({
    fields: taskFields,
    gridApi,
  });

  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setIsInitialLoading(true);
      setGridApi(event.api);
      setGridColumnApi(event.columnApi);
      event.api.setServerSideDatasource({
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, success, fail } = params;
          const { endRow, startRow, sortModel } = request;

          TaskAPI.getPaginatedTaskListForDashbaord({
            dashboardFilter,
            widgetFilter,
            startRow: startRow ?? 0,
            endRow: endRow ?? 100,
            widgetId,
            sort: getSortAsString(lookup, projectLookup, sortModel),
          })
            .then(({ count, data }) => {
              setIsInitialLoading(false);
              const { byId: taskLookup, ids: allMyIds } =
                constructNormalizedTask({
                  fieldList: taskFields,
                  isLoading: false,
                  taskList: data,
                });

              if (allMyIds[0] === 'loading') {
                return;
              }
              const tasks = toRows(allMyIds[1], taskLookup);

              const projectIds = data.map((t) => t.project.projectId);

              socketSubscriber(uniq(projectIds));
              const projects = transformProjectFields(projectFields, data);
              const taskWithProjects = tasks.map((task) => ({
                ...task,
                [EXTRA_DETAILS]: {
                  [SourceType.Project]: projects?.find(
                    ({ projectId }) => projectId === task.project,
                  ),
                },
              }));

              success({ rowData: taskWithProjects, rowCount: count });
            })
            .catch((e) => {
              setIsError(true);
              logger.log('error downloading content', e);
              setIsInitialLoading(false);
              fail();
            })
            .finally(() => {
              setIsInitialLoading(false);
            });
        },
      });
    },
    [
      dashboardFilter,
      widgetFilter,
      widgetId,
      lookup,
      projectLookup,
      taskFields,
      socketSubscriber,
      projectFields,
    ],
  );

  return {
    onGridReady,
    gridApi,
    isInitialLoading,
    gridColumnApi,
    isError,
  };
};
